import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { adminApi, getAdminToken } from "../../lib/adminApi";
import { useAuth } from "../../context/AuthContext";
import { allPermissions, normalizePermissions } from "../../lib/permissions";
import { PermissionMatrix } from "../../components/admin/PermissionMatrix";

const emptyForm = () => ({
  username: "",
  email: "",
  password: "",
  role: "admin",
  permissions: allPermissions(),
});

function Modal({ title, onClose, children, footer, wide }) {
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/45 grid place-items-end md:place-items-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className={`bg-white w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[92vh] overflow-hidden rounded-t-3xl md:rounded-3xl flex flex-col shadow-ambient`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-6 py-4 border-b border-black/8">
          <h2 className="font-display text-2xl">{title}</h2>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">{children}</div>
        {footer && <div className="shrink-0 px-6 py-4 border-t border-black/8 bg-white">{footer}</div>}
      </div>
    </div>
  );
}

export default function AdminAdminsPage() {
  const { isSuperadmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editPerms, setEditPerms] = useState(allPermissions());
  const [editSaving, setEditSaving] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [notice, setNotice] = useState("");

  const load = () =>
    adminApi
      .listAdmins(getAdminToken())
      .then(setAdmins)
      .catch((err) => setError(err.message));

  useEffect(() => {
    if (isSuperadmin) load();
  }, [isSuperadmin]);

  if (!isSuperadmin) return <Navigate to="/admin" replace />;

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await adminApi.createAdmin(getAdminToken(), {
        ...form,
        permissions: form.role === "superadmin" ? {} : form.permissions,
      });
      setForm(emptyForm());
      setNotice("Admin created.");
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (admin) => {
    setError("");
    try {
      await adminApi.setAdminActive(getAdminToken(), admin.id, !admin.is_active);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEdit = (admin) => {
    setError("");
    setEditing(admin);
    setEditPerms(normalizePermissions(admin.permissions));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setError("");
    try {
      await adminApi.updateAdminPermissions(getAdminToken(), editing.id, editPerms);
      setEditing(null);
      setNotice(`Permissions updated for ${editing.username}.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const openPassword = (admin) => {
    setPasswordTarget(admin);
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const savePassword = async () => {
    if (!passwordTarget) return;
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setPasswordSaving(true);
    setError("");
    try {
      await adminApi.setAdminPassword(getAdminToken(), passwordTarget.id, newPassword);
      setNotice(`Password updated for ${passwordTarget.username}.`);
      setPasswordTarget(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#EC4899]">Access</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Admins</h1>
      <p className="text-sm text-black/55 mt-2 max-w-3xl">
        Superadmins have full access. For other admins, tick which sidebar areas they can open, then tick which website and video contents they may edit.
      </p>

      {notice && !passwordTarget && !editing && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}
      {error && !passwordTarget && !editing && <p className="mt-4 text-sm text-[#BE185D]">{error}</p>}

      <form onSubmit={onCreate} className="mt-8 rounded-3xl bg-white border border-black/5 shadow-ambient p-6 md:p-8 space-y-6">
        <div>
          <h2 className="font-display text-2xl">Create admin</h2>
          <p className="text-sm text-black/55 mt-2">Only superadmins can add people who can sign in to this dashboard.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Username</span>
            <input
              className="cf-input mt-2"
              required
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Email</span>
            <input
              className="cf-input mt-2"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Password</span>
            <input
              className="cf-input mt-2"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Role</span>
            <select
              className="cf-input mt-2 bg-transparent"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="admin">admin (limited)</option>
              <option value="superadmin">superadmin (full access)</option>
            </select>
          </label>
        </div>

        {form.role === "admin" ? (
          <PermissionMatrix
            value={form.permissions}
            onChange={(permissions) => setForm({ ...form, permissions })}
          />
        ) : (
          <p className="text-sm text-black/60 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            Superadmins skip the permission list and can use every page, including creating other admins.
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 rounded-full bg-[#EC4899] text-white font-medium disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create admin"}
        </button>
      </form>

      <div className="mt-8 rounded-3xl bg-white border border-black/5 shadow-ambient overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="text-left font-mono text-[11px] uppercase tracking-widest text-black/45 border-b border-black/5">
            <tr>
              <th className="px-5 py-4">User</th>
              <th className="px-5 py-4">Role</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4" />
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.id} className="border-t border-black/5">
                <td className="px-5 py-4">
                  <div className="font-medium">{admin.username}</div>
                  <div className="text-xs text-black/50">{admin.email || "—"}</div>
                </td>
                <td className="px-5 py-4 font-mono text-xs uppercase">{admin.role}</td>
                <td className="px-5 py-4">{admin.is_active ? "Active" : "Disabled"}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap space-x-3">
                  <button
                    type="button"
                    onClick={() => openPassword(admin)}
                    className="text-sm text-black/70 hover:underline"
                  >
                    Change password
                  </button>
                  {admin.role !== "superadmin" && (
                    <button
                      type="button"
                      onClick={() => openEdit(admin)}
                      className="text-sm text-black/70 hover:underline"
                    >
                      Permissions
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleActive(admin)}
                    className="text-sm text-[#EC4899] hover:underline"
                  >
                    {admin.is_active ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal
          wide
          title={`Permissions for ${editing.username}`}
          onClose={() => setEditing(null)}
          footer={
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border border-black/15">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editSaving}
                className="px-5 py-2.5 rounded-full bg-[#EC4899] text-white disabled:opacity-50"
              >
                {editSaving ? "Saving…" : "Save permissions"}
              </button>
            </div>
          }
        >
          {error && <p className="text-sm text-[#BE185D] mb-4">{error}</p>}
          <PermissionMatrix value={editPerms} onChange={setEditPerms} />
        </Modal>
      )}

      {passwordTarget && (
        <Modal
          title={`Change password for ${passwordTarget.username}`}
          onClose={() => {
            setPasswordTarget(null);
            setNewPassword("");
            setConfirmPassword("");
            setError("");
          }}
          footer={
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPasswordTarget(null)}
                className="px-5 py-2.5 rounded-full border border-black/15"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePassword}
                disabled={passwordSaving}
                className="px-5 py-2.5 rounded-full bg-[#EC4899] text-white disabled:opacity-50"
              >
                {passwordSaving ? "Updating…" : "Update password"}
              </button>
            </div>
          }
        >
          {error && <p className="text-sm text-[#BE185D] mb-4">{error}</p>}
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">New password</span>
            <input
              className="cf-input mt-2"
              type="password"
              minLength={8}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </label>
          <label className="block mt-5">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Confirm password</span>
            <input
              className="cf-input mt-2"
              type="password"
              minLength={8}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </label>
        </Modal>
      )}
    </div>
  );
}
